import PropTypes from 'prop-types';
import React from 'react';

import { getPostTypes } from '../Blog/CardListing';
import Card from '../Common/Card';
import * as styles from './AdditionalResources.module.scss';
import PageSection from './PageSection';

export const ResourceCards = ({ content, showImage, useContentType = false, flipImage = true }) => {
  return content?.map((card, i) => {
    const type = getPostTypes(card, useContentType);
    switch (card.__typename) {
      case 'ContentfulComponentSimpleContent': {
        return (
          <Card
            key={i}
            url={card.linkUrl}
            ctaLabel={card.linkLabel}
            title={card.title}
            description={card.body}
            type={card.subtitle}
            className={styles.custom}
            image={showImage ? card.image : null}
            data-analytics-component={'resource-card'}
            data-analytics-id={card.title}
          />
        );
      }

      case 'ContentfulPageCaseStudy': {
        const image = card.pageMetaData?.ogImage || card.image;
        return (
          <Card
            key={i}
            url={`/${card.slug}`}
            ctaLabel="Read the story"
            title={card.heading}
            description={card.description?.description}
            type={type}
            className={styles.customerStory}
            image={showImage ? image : null}
            data-analytics-component={'resource-card'}
            data-analytics-id={card.title}
          />
        );
      }

      case 'ContentfulPageBlogPost': {
        let className = styles.blogPost;
        switch (card.contentType?.title) {
          case 'FullStory Engineering Blog':
            className = styles.engineeringPost;
            break;
          case 'FullStory Culture Blog':
            className = styles.culturePost;
            break;
          default:
            break;
        }

        return (
          <Card
            key={i}
            url={`/blog/${card.slug}`}
            ctaLabel={card.contentType?.viewLabel}
            title={card.title}
            description={card.articleDescription?.articleDescription}
            type={type}
            className={className}
            image={showImage ? card.pageMetaData?.ogImage : null}
            data-analytics-component={'resource-card'}
            data-analytics-id={card.title}
          />
        );
      }

      case 'ContentfulPageResource': {
        const typeTitle = card.contentType?.title || 'Resource';
        let className = 'guide';

        if (typeTitle === 'Product Snapshots') {
          className = 'productSnapshots';
        } else if (typeTitle === 'Video') {
          className = 'video';
        }

        return (
          <Card
            key={i}
            url={`/resources/${card.slug}`}
            ctaLabel={card.contentType?.viewLabel}
            title={card.title}
            description={card.articleDescription?.articleDescription}
            type={type}
            className={styles[className]}
            image={showImage ? card.pageMetaData?.ogImage : null}
            data-analytics-component={'resource-card'}
            data-analytics-id={card.title}
          />
        );
      }

      case 'ContentfulPageWalledResource': {
        return (
          <Card
            key={i}
            url={`/resources/${card.slug}`}
            ctaLabel={card.contentType?.viewLabel}
            title={card.title}
            description={card.description?.description}
            type={type}
            className={styles.resource}
            image={showImage ? card.pageMetaData?.ogImage : null}
            data-analytics-component={'resource-card'}
            data-analytics-id={card.title}
          />
        );
      }

      default:
        return null;
    }
  });
};

export default function AdditionalResources({
  section,
  showImage = false,
  cardColor,
  useContentType = false,
  ...rest
}) {
  return (
    <PageSection
      slim
      style={rest.style || {}}
      className={[
        styles.section,
        section.backgroundColor ? styles[section.backgroundColor.toLowerCase()] : '',
        rest?.className || '',
      ].join(' ')}
      data-analytics-section={'additional-resources'}
    >
      {section.title && <h2>{section.title}</h2>}
      {section.heading && <h2>{section.heading}</h2>}
      <div className={[styles.content, cardColor ? styles[cardColor] : ''].join(' ')}>
        <ResourceCards content={section.content} showImage={showImage} useContentType={useContentType} />
      </div>
    </PageSection>
  );
}

AdditionalResources.propTypes = {
  section: PropTypes.object.isRequired,
  showImage: PropTypes.bool,
  useContentType: PropTypes.bool,
  cardColor: PropTypes.oneOf(['white', 'alternate']),
};
